import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Loader2, BookOpen, Sparkles, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * ConceptEssayDialog — a modal that opens when a learner taps a
 * cross-world concept pill.  Fetches a 200-word Ms. Pradhan essay
 * explaining why the idea shows up in the given set of worlds.
 * Cached backend-side per (term, world signature).
 */
export default function ConceptEssayDialog({ term, worlds, onClose }) {
  const [body, setBody] = useState("");
  const [worldLinks, setWorldLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cached, setCached] = useState(false);

  useEffect(() => {
    if (!term || !worlds?.length) return;
    let cancelled = false;
    setLoading(true);
    setBody("");
    setWorldLinks([]);
    const slugs = worlds.map((w) => w.slug).join(",");
    api.get(`/concepts/${encodeURIComponent(term)}/essay?worlds=${encodeURIComponent(slugs)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setBody(data.body || "");
        setWorldLinks(data.world_links || []);
        setCached(!!data.cached);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || "Ms. Pradhan couldn't write this one just now.";
        toast.error(msg);
        setBody(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [term, worlds]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="concept-essay-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl rounded-3xl border-2 border-ink bg-cream shadow-[10px_10px_0_0_#1F1A17] overflow-hidden max-h-[85vh] overflow-y-auto"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full border-2 border-ink bg-cream hover:bg-marigold hover:text-cream text-ink flex items-center justify-center transition"
          data-testid="concept-essay-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-7 md:p-9">
          <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-marigold mb-2 inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Ms. Pradhan explains
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-ink leading-tight mb-2 capitalize" data-testid="concept-essay-title">
            {term}
          </h2>
          <div className="flex flex-wrap gap-1.5 mb-6" data-testid="concept-essay-worlds">
            {worlds.map((w) => (
              <span key={w.slug} className="text-[10px] font-mono uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-full border border-ink/30 text-ink2">
                {w.kicker} · {w.title}
              </span>
            ))}
          </div>

          {loading ? (
            <div className="py-10 text-center text-ink2" data-testid="concept-essay-loading">
              <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2 text-marigold" />
              <div className="text-sm">Ms. Pradhan is thinking about why this idea keeps coming up…</div>
              <div className="text-xs text-ink2/60 mt-1 italic">(takes ~10 seconds the first time; instant after that)</div>
            </div>
          ) : (
            <article
              className="prose prose-slate max-w-none prose-p:text-ink prose-p:leading-[1.75] prose-p:text-base whitespace-pre-line font-heading"
              data-testid="concept-essay-body"
            >
              {body.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
            </article>
          )}

          {!loading && worldLinks.length > 0 && (
            <div className="mt-6" data-testid="concept-essay-world-links">
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-2">
                Read the idea in each world →
              </div>
              <div className="flex flex-col gap-1.5">
                {worldLinks.map((l) => (
                  <Link
                    key={l.world_slug + l.chapter_slug}
                    to={`/library/${l.chapter_slug}`}
                    className="group flex items-center gap-3 rounded-xl border border-ink/25 hover:border-marigold hover:-translate-y-0.5 hover:shadow-[3px_3px_0_0_#1F1A17] transition-all p-3"
                    data-testid={`concept-essay-link-${l.world_slug}`}
                  >
                    <span className="text-[9px] font-mono font-bold text-marigold uppercase tracking-[0.22em] w-16 flex-shrink-0">{l.world_kicker}</span>
                    <span className="flex-1 min-w-0">
                      <span className="text-[10px] font-mono text-ink/50 mr-2">{l.chapter_code}</span>
                      <span className="text-sm font-heading font-semibold text-ink group-hover:text-marigold transition">{l.chapter_title}</span>
                    </span>
                    <ArrowRight className="w-4 h-4 text-ink/40 group-hover:text-marigold group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {!loading && (
            <div className="mt-6 pt-4 border-t border-ink/20 flex items-center justify-between gap-3 text-[10px] uppercase tracking-[0.24em] font-bold text-ink2/60">
              <span className="inline-flex items-center gap-1.5">
                <BookOpen className="w-3 h-3" /> {cached ? "From Ms. Pradhan's notebook" : "Freshly written for you"}
              </span>
              <span>Claude Sonnet 4.5 · under her signature</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
